/**
 * Grid layout utilities for VideoGrid.
 * HA channels (expectedStreams === 2) = 2 slots; single = 1 slot.
 */

/**
 * Calculate total grid slots from channel configs.
 * @param {Array<{expectedStreams?: number}>} channels
 * @returns {number}
 */
export function getTotalSlots(channels) {
  return (channels || []).reduce(
    (sum, ch) => sum + (ch.expectedStreams === 2 ? 2 : 1),
    0
  );
}

/**
 * Calculate grid dimensions (rows, cols) for a given slot count.
 * @param {number} count - Total number of slots
 * @returns {{ rows: number, cols: number }}
 */
export function calculateGridDimensions(count) {
  const sqrt = Math.sqrt(count);
  const cols = Math.ceil(sqrt);
  const rows = Math.ceil(count / cols);
  return { rows, cols };
}

/**
 * Compute grid placement for each channel so regular channels fill their row
 * when the next channel (HA) would wrap, avoiding empty slots.
 * @param {Array<{expectedStreams?: number}>} channels
 * @param {{ rows: number, cols: number }} dimensions - from calculateGridDimensions(getTotalSlots(channels))
 * @returns {Array<{ gridRow: number, gridColumn: number, gridColumnSpan: number }>}
 */
export function getChannelPlacements(channels, dimensions) {
  const { rows, cols } = dimensions;
  const placements = [];
  let row = 0;
  let col = 0;

  for (let i = 0; i < channels.length; i++) {
    const ch = channels[i];
    const slots = ch.expectedStreams === 2 ? 2 : 1;
    const nextSlots = i + 1 < channels.length
      ? (channels[i + 1].expectedStreams === 2 ? 2 : 1)
      : 0;

    let span = slots;
    if (slots === 1 && nextSlots === 2 && cols - col <= nextSlots) {
      span = cols - col;
    }

    placements.push({
      gridRow: row + 1,
      gridColumn: col + 1,
      gridColumnSpan: span
    });

    col += span;
    if (col >= cols) {
      col = 0;
      row++;
    }
  }

  return placements;
}
